import { useEffect, useState } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs, { Dayjs } from "dayjs";
import { CustomButton } from "src/design/CustomButton";
import { CustomLoadingButton } from "src/design/CustomLoadingButton";
import { colorTextMuted } from "src/design/tokens";
import { useUpdateSubscription } from "src/data/admin/queries/subscription";
import {
  AdminRestaurantSubscriptionDto,
  UpdateSubscriptionRequest,
} from "src/data/admin/types/subscription";

interface EditSubscriptionDialogProps {
  /** The subscription to edit; `null` keeps the dialog closed. */
  subscription: AdminRestaurantSubscriptionDto | null;
  /** Tier names from the public catalog. */
  tiers: string[];
  onClose: () => void;
  onUpdated: (message: string) => void;
  onError: (message: string) => void;
}

/**
 * Partial update of an existing subscription (`PUT`) — move the tier, push the
 * expiration date, or flip auto-renewal. Only the fields the admin changed are
 * sent.
 */
export const EditSubscriptionDialog = ({
  subscription,
  tiers,
  onClose,
  onUpdated,
  onError,
}: EditSubscriptionDialogProps) => {
  const [tier, setTier] = useState("");
  const [expiresOn, setExpiresOn] = useState<Dayjs | null>(null);
  const [autoRenewal, setAutoRenewal] = useState(true);

  useEffect(() => {
    if (!subscription) return;
    setTier(subscription.tier);
    setExpiresOn(
      subscription.nextBillingCycle
        ? dayjs(subscription.nextBillingCycle)
        : null,
    );
    setAutoRenewal(subscription.autoRenewal);
  }, [subscription]);

  const updateMutation = useUpdateSubscription({
    onSuccess: (updated) => {
      onUpdated(`${updated.restaurantName}'s subscription has been updated.`);
      onClose();
    },
  });

  // The catalog may not contain a legacy tier the record still points at.
  const tierOptions = tiers.includes(subscription?.tier ?? "")
    ? tiers
    : [...tiers, subscription?.tier ?? ""].filter(Boolean);

  const isDateValid = expiresOn === null || expiresOn.isValid();
  const nextBillingCycle = expiresOn?.format("YYYY-MM-DD") ?? null;
  const hasChanges =
    !!subscription &&
    (tier !== subscription.tier ||
      autoRenewal !== subscription.autoRenewal ||
      nextBillingCycle !== subscription.nextBillingCycle);

  const handleSubmit = () => {
    if (!subscription || !hasChanges || !isDateValid) return;

    const payload: UpdateSubscriptionRequest = {};
    if (tier !== subscription.tier) payload.tier = tier;
    if (autoRenewal !== subscription.autoRenewal)
      payload.autoRenewal = autoRenewal;
    if (nextBillingCycle && nextBillingCycle !== subscription.nextBillingCycle)
      payload.nextBillingCycle = nextBillingCycle;

    updateMutation.mutate(
      { restaurantId: subscription.restaurantId, payload },
      {
        onError: (error) =>
          onError(
            error instanceof Error
              ? error.message
              : "Failed to update the subscription",
          ),
      },
    );
  };

  return (
    <Dialog
      open={!!subscription}
      onClose={() => !updateMutation.isPending && onClose()}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ fontWeight: 600 }}>
        Update {subscription?.restaurantName}'s subscription
      </DialogTitle>
      <DialogContent>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Tier</InputLabel>
                <Select
                  label="Tier"
                  value={tier}
                  onChange={(event) => setTier(event.target.value)}
                >
                  {tierOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <DatePicker
                label="Expires on"
                value={expiresOn}
                onChange={(value) => setExpiresOn(value)}
                renderInput={(params) => (
                  <TextField {...params} fullWidth size="small" />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={autoRenewal}
                    onChange={(event) => setAutoRenewal(event.target.checked)}
                  />
                }
                label="Auto-renewal"
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="caption" sx={{ color: colorTextMuted }}>
                Access ends 3 days after the expiration date. Clearing the date
                is not supported here — grant the subscription again to reset
                it.
              </Typography>
            </Grid>
          </Grid>
        </LocalizationProvider>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <CustomButton
          color="inverted"
          onClick={onClose}
          disabled={updateMutation.isPending}
        >
          Cancel
        </CustomButton>
        <CustomLoadingButton
          loading={updateMutation.isPending}
          disabled={!hasChanges || !isDateValid}
          onSubmit={handleSubmit}
          label="Save"
          color="success"
          fullwidth={false}
        />
      </DialogActions>
    </Dialog>
  );
};
